import { MissionIcon, NegativeThumb, PersonIcon, PositiveThumb } from 'assets/icons';
import Badge from 'components/badge/badge';
import CountBadge from 'components/badge/CountBadge';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import PillTabs from 'components/tabs/PillTabs';
import useText from 'hooks/useText';
import { missionPeriod } from 'pages/companies/recordItems';
import { useState } from 'react';

// The groups of missions, in this order, with the tone of their icon.
const GROUPS = [
  ['current', 'bg-green-50 text-green-700'],
  ['upcoming', 'bg-gray-100 text-gray-700'],
  ['done', 'bg-red-50 text-red-700'],
];

// The groups of temp workers by the reviews the company left them, best
// first, with the icon and tone of their header.
const WORKER_GROUPS = [
  ['positive', PositiveThumb, 'bg-green-50 text-green-700'],
  ['mixed', PersonIcon, 'bg-gray-100 text-gray-700'],
  ['negative', NegativeThumb, 'bg-red-50 text-red-700'],
  ['notRated', PersonIcon, 'bg-gray-100 text-gray-700'],
];

const ratingBalance = ({ positive = 0, negative = 0 }) => positive - negative;

const workerRating = ({ positive = 0, negative = 0 }) => {
  if (!positive && !negative) return 'notRated';
  if (positive > negative) return 'positive';
  if (negative > positive) return 'negative';
  return 'mixed';
};

// A group of cards under its header, which sticks below the record's tabs
// (34 px high) while its cards scroll.
const Group = ({ icon: Icon, tone, label, count, children }) => (
  <div className="flex flex-col gap-2">
    <div className="sticky top-[34px] z-10 flex items-center gap-3 bg-white px-3 py-1.5">
      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>
        <Icon wh={18} color="currentColor" />
      </span>
      <p className="flex-1 text-sm font-medium uppercase text-gray-700">{label}</p>
      <CountBadge value={count} variant="outline" />
    </div>
    {children}
  </div>
);

const Card = ({ title, subtitle, tag, right }) => (
  <div className="flex items-center gap-4 rounded-xl border border-gray-100 px-4 py-3">
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium text-gray-900">{title}</p>
      <p className="truncate text-sm text-gray-700">{subtitle}</p>
    </div>
    <div className="flex shrink-0 flex-col items-end gap-1">
      {tag}
      {right}
    </div>
  </div>
);

/**
 * The « Missions » of a record, as on the prototype: the missions under way,
 * to come and over, each with its temp worker and its period; or the temp
 * workers who worked there, with the reviews they got. `initial`: the view
 * shown first (`workers` for the temp workers). `action`: a button at the
 * right of the two views (« Créer une mission » on a company).
 */
const RecordMissions = ({ missions, tempWorkers, initial = 'missions', action = null }) => {
  const { text } = useText('record');
  const [view, setView] = useState(initial);
  return (
    <div className="flex flex-col gap-5 pb-6">
      <div className="flex items-center gap-3">
        <PillTabs
          activeTab={view}
          onChange={setView}
          tabs={[
            { id: 'missions', label: text('missionsTab') },
            { id: 'workers', label: text('workersTab') },
          ]}
        />
        {action && <div className="ml-auto shrink-0">{action}</div>}
      </div>
      {view === 'missions' && !missions.length && (
        <CompactEmptyState compact glyph="inbox" title={text('missionsEmptyTitle')} description={text('missionsEmptyText')} />
      )}
      {view === 'missions' &&
        GROUPS.map(([state, tone]) => {
          const list = missions.filter((mission) => mission.state === state);
          if (!list.length) return null;
          return (
            <Group key={state} icon={MissionIcon} tone={tone} label={text(`missions_${state}`)} count={list.length}>
              {list.map((mission) => (
                <Card
                  key={mission.id}
                  title={mission.title}
                  subtitle={[mission.companyName, mission.worker || text('noWorker')].filter(Boolean).join(' · ')}
                  tag={mission.number ? <Badge type={state === 'current' ? 'VALID' : 'PRIMARY'}>{`N°${mission.number}`}</Badge> : null}
                  right={<p className="text-xs text-gray-600">{missionPeriod(mission)}</p>}
                />
              ))}
            </Group>
          );
        })}
      {view === 'workers' && !tempWorkers.length && (
        <CompactEmptyState compact glyph="inbox" title={text('workersEmptyTitle')} description={text('workersEmptyText')} />
      )}
      {view === 'workers' &&
        WORKER_GROUPS.map(([rating, icon, tone]) => {
          const list = tempWorkers
            .filter((worker) => workerRating(worker) === rating)
            .sort((a, b) => ratingBalance(b) - ratingBalance(a) || b.missions - a.missions);
          if (!list.length) return null;
          return (
            <Group key={rating} icon={icon} tone={tone} label={text(`workers_${rating}`)} count={list.length}>
              {list.map((worker) => (
                <Card
                  key={worker.id}
                  title={worker.name}
                  subtitle={text(worker.missions > 1 ? 'workerMissionsMany' : 'workerMissionsOne', { count: worker.missions })}
                  right={
                    <div className="flex items-center gap-2">
                      {worker.positive > 0 && (
                        <Badge type="VALID">
                          <PositiveThumb wh={12} color="currentColor" /> {worker.positive}
                        </Badge>
                      )}
                      {worker.negative > 0 && (
                        <Badge type="ALERT">
                          <NegativeThumb wh={12} color="currentColor" /> {worker.negative}
                        </Badge>
                      )}
                      {!worker.positive && !worker.negative && <span className="text-xs text-gray-600">{text('notRated')}</span>}
                    </div>
                  }
                />
              ))}
            </Group>
          );
        })}
    </div>
  );
};

export default RecordMissions;
